import { AccessToken, Configuration } from '@shared/sdk-v1';

const AUTH_KEYS = ['accessToken', 'user', 'tokenExpiration', 'roles'];

const clearAuthAndRedirect = () => {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
  window.location.href = '/auth';
};

// A 401 from these endpoints means the credentials the *user just typed* were
// rejected — NOT that the user's own session expired. They must not force a
// logout/redirect: the user is already sitting on /auth, so re-assigning the
// same URL triggers a full document navigation that reads as an unexplained
// page refresh and tears down the error toast before it can be read. Exempting
// them lets the calling component surface an inline error instead.
const SESSION_AGNOSTIC_401_ENDPOINTS = [
  /\/CourierUsers\/login\b/,
  // A rejected passkey assertion is a failed sign-in attempt, not an expired
  // session — clearing storage and redirecting here would wipe the error
  // before the user could read it.
  /\/CourierUsers\/passkey\/login-(options|verify)\b/,
];

const isSessionAgnostic401 = (url: string) =>
  SESSION_AGNOSTIC_401_ENDPOINTS.some((pattern) => pattern.test(url));

const expiredTokenMiddleware = {
  post: async (context: { response: Response }) => {
    if (
      context.response.status === 401 &&
      !isSessionAgnostic401(context.response.url || '')
    ) {
      clearAuthAndRedirect();
    }
    return context.response;
  },
};

export const apiConfig = (forceToken?: string) => {
  if (forceToken) {
    return new Configuration({
      basePath: import.meta.env.VITE_API_BASE_URL || 'http://localhost:9001',
      accessToken: forceToken,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${forceToken}`,
        'X-API-Key': `0d7f4b1b-0d7f-4b1b-9d0f-8b0f3f7c6f2a`,
      },
    });
  }

  const tokenExpiration = localStorage.getItem('tokenExpiration');
  if (tokenExpiration && new Date(tokenExpiration) <= new Date()) {
    clearAuthAndRedirect();
    return new Configuration({
      basePath: import.meta.env.VITE_API_BASE_URL || 'http://localhost:9001',
    });
  }

  const accessTokenString = localStorage.getItem('accessToken');
  const accessToken: AccessToken = accessTokenString
    ? JSON.parse(accessTokenString)
    : undefined;
  return new Configuration({
    basePath: import.meta.env.VITE_API_BASE_URL || 'http://localhost:9001',
    accessToken: accessToken?.id ?? undefined,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `${accessToken?.id}`,
      'X-API-Key': `0d7f4b1b-0d7f-4b1b-9d0f-8b0f3f7c6f2a`,
    },
    middleware: [expiredTokenMiddleware],
  });
};
